import React from 'react';
import { createRoot } from 'react-dom/client';
import Icicle from './Pages/Sos2/Icicle';

// Standalone mount for the icicle/timeline view (/icicle?id=X). Reads the same movie payload the SoS app
// gets (data-props JSON, built by AnalysisController::icicle via buildRatingsFromDer).
//
// Mount targets — TWO patterns:
//   #icicle-widget           — standalone icicle page and in-page LR partial (single widget)
//   [data-lr-widget]         — LR LP section (multiple widgets, one per finding tab)
//
// LP behavior: only the currently-active tab's widget mounts at load. Others
// mount lazily when their tab is activated. This means a broken data payload
// on an inactive tab can't crash the page — the crash is scoped to the tab
// that surfaces it, and its placeholder is shown instead of the widget.
function _describeWidget(el) {
  return {
    id: el.id || '(no id)',
    findingKey: el.dataset.lrFindingKey || '(unknown)',
    movieTitle: el.dataset.lrMovieTitle || '(unknown)',
    movieId: el.dataset.lrMovieId || '(unknown)',
  };
}

function _renderMountError(el, err) {
  const info = _describeWidget(el);
  console.error(
    '[lr-widget mount failed] finding=%s movie="%s" (id=%s) element=%s',
    info.findingKey, info.movieTitle, info.movieId, info.id,
    err
  );
  el.innerHTML = ''
    + '<div style="padding:24px;text-align:center;color:#7a7770;">'
    +   '<div style="font-weight:700;color:#14536e;margin-bottom:8px;">Widget error</div>'
    +   '<div style="font-size:14px;line-height:1.5;">'
    +     'This film’s Layered Read data couldn’t render. '
    +     'See the browser console for details; try another tab.'
    +   '</div>'
    +   '<div style="font-size:12px;color:#a49f95;margin-top:12px;">'
    +     'finding: <code>' + info.findingKey + '</code> · '
    +     'movie: <code>' + info.movieTitle + '</code>'
    +   '</div>'
    + '</div>';
  el.dataset.lrMountFailed = '1';
}

function mountIcicle(el) {
  if (!el || el.dataset.lrMounted === '1' || el.dataset.lrMountFailed === '1') return;
  el.dataset.lrMounted = '1';
  let props;
  try {
    props = JSON.parse(el.dataset.props || '{}');
  } catch (e) {
    _renderMountError(el, e);
    return;
  }
  const embedded = el.dataset.embedded === '1';
  const publicView = el.dataset.publicView === '1';
  const initialMetric = el.dataset.initialMetric || null;
  const preGrain = el.dataset.preselectGrain || null;
  const preLabel = el.dataset.preselectLabel || null;
  const initialSelection = (preGrain && preLabel) ? { grain: preGrain, label: preLabel } : null;
  // `?lr=` deep link (grammar: Pages/Sos2/deepLink.js). Only the page's own widget (#icicle-widget)
  // reads the URL — the LP's per-finding widgets are demos and must not all jump to one target.
  // It also listens for `sr:navigate` (window.srNavigate) so in-page links reach it without a reload;
  // `window.__srPendingLr` carries a request made before this widget mounted.
  let initialTarget = null;
  const listenNavigate = el.id === 'icicle-widget';
  if (listenNavigate) {
    try { initialTarget = new URLSearchParams(window.location.search).get('lr') || null; } catch (e) { initialTarget = null; }
    if (!initialTarget && window.__srPendingLr) initialTarget = String(window.__srPendingLr);
    window.__srPendingLr = null;
  }

  try {
    const root = createRoot(el);
    // Wrap in an error boundary so a render-time crash inside Icicle also
    // localizes to this tab. Class component is inline to avoid another file.
    class Boundary extends React.Component {
      constructor(p) { super(p); this.state = { err: null }; }
      static getDerivedStateFromError(err) { return { err }; }
      componentDidCatch(err) { _renderMountError(el, err); }
      render() { return this.state.err ? null : this.props.children; }
    }
    root.render(
      <Boundary>
        <Icicle movie={props} embedded={embedded} publicView={publicView}
                initialMetric={initialMetric} initialSelection={initialSelection}
                initialTarget={initialTarget} listenNavigate={listenNavigate} />
      </Boundary>
    );
  } catch (e) {
    _renderMountError(el, e);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Legacy single-widget page mount.
  const legacy = document.getElementById('icicle-widget');
  if (legacy) mountIcicle(legacy);
  // LR LP: mount only the widget inside the currently-active tab panel.
  // Others mount lazily via __mountLrWidget when their tab activates.
  const activePanel = document.querySelector('.lr-lp3__grid--active');
  if (activePanel) {
    activePanel.querySelectorAll('[data-lr-widget]').forEach(mountIcicle);
  }
});

// Exposed for the LR LP section: when a tab activates its widget the first
// time, the partial's JS calls this to mount on demand.
window.__mountLrWidget = mountIcicle;
